import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, CheckCircle2, Users2 } from 'lucide-react';

/**
 * Reusable "Add/Manage clients in subtask" picker.
 *
 * Props:
 *   open            boolean — dialog open state
 *   onOpenChange    (bool) => void
 *   clients         Array<{id, name, company_name?, pan?, gstins?: Array<string|{number}>}>
 *   linkedIds       Set<string> | Array<string> — already-linked client ids (shown ticked initially)
 *   onApply         (ids: string[]) => Promise<void> — called with the FINAL selected ids
 *   title           string (default: "Manage clients")
 *   subtitle        string (optional)
 */
export const ClientPickerDialog = ({
  open,
  onOpenChange,
  clients = [],
  linkedIds = [],
  onApply,
  title = 'Manage clients',
  subtitle,
}) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set(Array.isArray(linkedIds) ? linkedIds : [...linkedIds]));
  const [applying, setApplying] = useState(false);

  // Reset selection state when the dialog is opened with a new set of linked ids
  useEffect(() => {
    if (open) {
      setSelected(new Set(Array.isArray(linkedIds) ? linkedIds : [...linkedIds]));
      setSearch('');
    }
  }, [open, linkedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(cl => {
      const hay = [
        cl.name || '',
        cl.company_name || '',
        cl.pan || '',
        ...((cl.gstins || []).map(g => (typeof g === 'string' ? g : g?.number || ''))),
      ];
      return hay.some(h => h.toLowerCase().includes(q));
    });
  }, [clients, search]);

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(c => next.add(c.id));
      return next;
    });
  };

  const deselectAllFiltered = () => {
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(c => next.delete(c.id));
      return next;
    });
  };

  const handleApply = async () => {
    if (!onApply) return;
    setApplying(true);
    try {
      await onApply([...selected]);
      onOpenChange(false);
    } finally {
      setApplying(false);
    }
  };

  const initialIds = new Set(Array.isArray(linkedIds) ? linkedIds : [...linkedIds]);
  const added = [...selected].filter(id => !initialIds.has(id)).length;
  const removed = [...initialIds].filter(id => !selected.has(id)).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 flex flex-col max-h-[85vh]" data-testid="client-picker-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Users2 className="h-5 w-5" /> {title}
          </DialogTitle>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </DialogHeader>

        <div className="px-5 pt-3 pb-2 border-b bg-muted/20">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Search by name, company, PAN, or GSTIN number..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid="client-picker-search"
            />
          </div>
          <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
            <span data-testid="client-picker-count">
              Showing {filtered.length} of {clients.length} clients
              {selected.size > 0 && ` · ${selected.size} selected`}
            </span>
            <div className="flex gap-3">
              <button onClick={selectAllFiltered} className="hover:text-primary transition-colors" type="button">Select all {search && `(filtered)`}</button>
              <button onClick={deselectAllFiltered} className="hover:text-primary transition-colors" type="button">Deselect all {search && `(filtered)`}</button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-[320px]" data-testid="client-picker-list">
          {filtered.length === 0 ? (
            <div className="px-6 py-12 text-sm text-muted-foreground text-center">
              No clients match "{search}"
            </div>
          ) : (
            <ul className="divide-y">
              {filtered.map(cl => {
                const isSelected = selected.has(cl.id);
                const wasLinked = initialIds.has(cl.id);
                return (
                  <li key={cl.id}>
                    <button
                      type="button"
                      onClick={() => toggleOne(cl.id)}
                      className={`w-full text-left flex items-center gap-3 px-5 py-2.5 transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'}`}
                      data-testid={`client-picker-row-${cl.id}`}
                    >
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleOne(cl.id)} onClick={(e) => e.stopPropagation()} />
                      <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary flex-shrink-0">
                        {(cl.name || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{cl.name}</div>
                        {(cl.company_name || cl.pan) && (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {cl.company_name && <span>{cl.company_name}</span>}
                            {cl.company_name && cl.pan && <span className="mx-1">·</span>}
                            {cl.pan && <span className="font-mono">{cl.pan}</span>}
                          </div>
                        )}
                      </div>
                      {wasLinked && (
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> linked
                        </Badge>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t bg-muted/20 flex items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground">
            {added > 0 && <span className="text-emerald-600 font-medium">+{added} to add</span>}
            {added > 0 && removed > 0 && <span className="mx-2">·</span>}
            {removed > 0 && <span className="text-red-600 font-medium">−{removed} to remove</span>}
            {added === 0 && removed === 0 && <span>No changes</span>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={applying} data-testid="client-picker-cancel">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={applying || (added === 0 && removed === 0)}
              data-testid="client-picker-apply"
            >
              {applying ? 'Saving…' : 'Apply changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClientPickerDialog;
